import { deserialize, DocumentObject } from '@jsonapiworld/jsonapi-serializer';
import { backendApiGateway } from '~/client/backend';
import { Company } from '~/types/company';
import { CompanyFixedAssetSpecies, FixedAssetSpeciesCategory } from '~/types/fixed-asset';
import { buildJsonAPIQueryParams, jsonAPIIncludeBuilder } from '~/utils/jsonapi-query';

export type FixedAssetSpeciesCategoryAccountsPayload = Partial<
  Pick<FixedAssetSpeciesCategory, 'assetAccountId' | 'depreciationAccountId' | 'accumulatedDepreciationAccountId'>
>;

export const fetchFixedAssetsSpecies = async (companyId: Company['id']): Promise<CompanyFixedAssetSpecies[]> => {
  const includeBuilder = jsonAPIIncludeBuilder([
    'species',
    'species.group',
    'species.speciesCategories',
    'species.speciesCategories.category',
  ]);
  const params = buildJsonAPIQueryParams(includeBuilder);
  const { data } = await backendApiGateway.get<DocumentObject>(`/api/v1/companies/${companyId}/fixed-asset-species`, {
    params,
  });
  return deserialize<CompanyFixedAssetSpecies>(data) as CompanyFixedAssetSpecies[];
};

export const updateFixedAssetsSpeciesCategoryAccounts = (
  companyId: Company['id'],
  speciesCategoryId: FixedAssetSpeciesCategory['id'],
  accounts: FixedAssetSpeciesCategoryAccountsPayload,
): Promise<void> => {
  return backendApiGateway.patch(
    `/api/v1/companies/${companyId}/fixed-asset-species-categories/${speciesCategoryId}`,
    {
      data: {
        type: 'fixed-asset-species-category',
        id: speciesCategoryId,
        attributes: accounts,
      },
    },
    {
      headers: {
        'content-type': 'application/vnd.api+json',
      },
    },
  );
};

export const deleteFixedAssetsSpecies = (companyId: Company['id'], speciesId: string): Promise<void> => {
  return backendApiGateway.delete(`/api/v1/companies/${companyId}/fixed-asset-species/${speciesId}`);
};

export const createFixedAssetsSpecies = async (
  companyId: Company['id'],
  speciesId: string,
): Promise<CompanyFixedAssetSpecies> => {
  const { data } = await backendApiGateway.post<CompanyFixedAssetSpecies>(
    `/api/v1/companies/${companyId}/fixed-asset-species`,
    {
      data: {
        type: 'company-fixed-asset-species',
        attributes: {},
        relationships: {
          species: {
            data: {
              type: 'fixed-asset-species',
              id: speciesId,
            },
          },
        },
      },
    },
    {
      headers: {
        'content-type': 'application/vnd.api+json',
      },
    },
  );
  return data;
};
